import { useEffect, useState } from "react";
import { ChevronLeft, FileDown, Eye } from "lucide-react";
import client, { extractError } from "@/api";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const STATUS_LABEL = { DRAFT: "Draft", ISSUED: "Issued", CANCELLED: "Cancelled" };
const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "DRAFT", label: "Draft" },
  { value: "ISSUED", label: "Issued" },
  { value: "CANCELLED", label: "Cancelled" },
];

function InvoiceDetail({ id, onBack }) {
  const [inv, setInv] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    client
      .get(`/admin/counterparty-invoices/${id}`)
      .then((r) => setInv(r.data))
      .catch((err) => setError(extractError(err)));
  }, [id]);
  if (error) return <div className="form-error">{error}</div>;
  if (!inv) return <div className="loading-block">Loading…</div>;
  const download = () =>
    window.open(`${API}/api/admin/counterparty-invoices/${id}/pdf`, "_blank");
  return (
    <div className="admin-company-detail" data-testid="admin-trade-doc-detail">
      <button type="button" className="link-button back" onClick={onBack}>
        <ChevronLeft size={14} /> Back to trade documents
      </button>
      <header className="detail-header">
        <div>
          <p className="eyebrow">SELLER → BUYER COMMERCIAL TAX INVOICE</p>
          <h2>{inv.invoice_number}</h2>
          <p className="muted">
            Admin review only — Barter India is <strong>not</strong> a party to this document.
          </p>
        </div>
        <div className="chip-stack">
          <span
            className={`status-chip status-${inv.status.toLowerCase()}`}
            data-testid="admin-trade-doc-status"
          >{STATUS_LABEL[inv.status] || inv.status}</span>
          <button
            type="button"
            className="primary-button"
            onClick={download}
            data-testid="admin-trade-doc-download"
          >
            <FileDown size={13} /> Download PDF
          </button>
        </div>
      </header>
      <section className="detail-grid">
        <article className="wide">
          <p className="eyebrow">SELLER (SUPPLIER)</p>
          <p><strong data-testid="admin-trade-doc-seller-name">{inv.seller_name}</strong></p>
          <p className="muted small">{inv.seller_address}</p>
          <p className="muted small">GSTIN: {inv.seller_gstin || "—"}</p>
        </article>
        <article className="wide">
          <p className="eyebrow">BUYER (RECIPIENT)</p>
          <p><strong data-testid="admin-trade-doc-buyer-name">{inv.buyer_name}</strong></p>
          <p className="muted small">{inv.buyer_address}</p>
          <p className="muted small">GSTIN: {inv.buyer_gstin || "—"}</p>
        </article>
      </section>
      <section className="section-block">
        <p className="eyebrow">SUPPLY DETAILS</p>
        <ul className="finance-list">
          <li>
            <span>Taxable value</span>
            <strong>₹{inv.taxable_value.toLocaleString("en-IN")}</strong>
          </li>
          {inv.is_interstate ? (
            <li>
              <span>IGST ({inv.gst_rate_percent}%)</span>
              <strong>₹{inv.igst_amount.toLocaleString("en-IN")}</strong>
            </li>
          ) : (
            <>
              <li>
                <span>CGST ({inv.gst_rate_percent / 2}%)</span>
                <strong>₹{inv.cgst_amount.toLocaleString("en-IN")}</strong>
              </li>
              <li>
                <span>SGST ({inv.gst_rate_percent / 2}%)</span>
                <strong>₹{inv.sgst_amount.toLocaleString("en-IN")}</strong>
              </li>
            </>
          )}
          <li className="total">
            <span>Total invoice value</span>
            <strong>₹{inv.total.toLocaleString("en-IN")}</strong>
          </li>
        </ul>
      </section>
      <section className="detail-grid">
        <article>
          <p className="eyebrow">INVOICE DATE</p>
          <p>{inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString() : "—"}</p>
        </article>
        <article>
          <p className="eyebrow">ISSUED AT</p>
          <p>{inv.issued_at ? new Date(inv.issued_at).toLocaleString() : "—"}</p>
        </article>
        <article>
          <p className="eyebrow">CANCELLED AT</p>
          <p>{inv.cancelled_at ? new Date(inv.cancelled_at).toLocaleString() : "—"}</p>
        </article>
      </section>
    </div>
  );
}

export default function AdminTradeDocumentsPage() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      params.set("page", page);
      params.set("page_size", 25);
      const r = await client.get(`/admin/counterparty-invoices?${params.toString()}`);
      setItems(r.data.items || []);
      setTotalPages(r.data.total_pages || 1);
      setTotal(r.data.total || 0);
    } catch (err) {
      setError(extractError(err, "Unable to load trade documents."));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status, page]);

  if (detail) {
    return (
      <InvoiceDetail
        id={detail}
        onBack={() => { setDetail(null); load(); }}
      />
    );
  }

  return (
    <div className="admin-listings-page" data-testid="admin-trade-documents-page">
      <div className="section-heading">
        <div>
          <p className="eyebrow">TRADE DOCUMENTS</p>
          <h3>Seller → Buyer commercial tax invoices</h3>
          <p className="muted small">
            Underlying commercial tax invoices between seller and buyer.
            Barter India is <strong>not</strong> a party to these invoices.
            These are separate from Barter India's facilitation fee invoices.
          </p>
        </div>
      </div>

      <div className="admin-toolbar">
        <div className="filter-tabs" data-testid="admin-trade-doc-tabs">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value || "all"}
              type="button"
              data-testid={`admin-trade-doc-filter-${f.value || "all"}`}
              className={status === f.value ? "active" : ""}
              onClick={() => { setStatus(f.value); setPage(1); }}
            >{f.label}</button>
          ))}
        </div>
        <small className="muted" data-testid="admin-trade-doc-total">
          {total.toLocaleString("en-IN")} document{total === 1 ? "" : "s"}
        </small>
      </div>

      {error && <div className="form-error" data-testid="admin-trade-doc-error">{error}</div>}

      {loading ? (
        <div className="loading-block">Loading…</div>
      ) : (
        <>
          <table className="admin-table" data-testid="admin-trade-documents-table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Seller</th>
                <th>Buyer</th>
                <th>Taxable Value</th>
                <th>GST</th>
                <th>Total</th>
                <th>Status</th>
                <th>Invoice Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={9} className="empty-row" data-testid="admin-trade-doc-empty">
                    No counterparty invoices yet.
                  </td>
                </tr>
              )}
              {items.map((i) => (
                <tr key={i.id} data-testid={`admin-trade-doc-row-${i.id}`}>
                  <td><strong>{i.invoice_number}</strong></td>
                  <td>
                    {i.seller_name}
                    <br />
                    <small className="muted">{i.seller_gstin || "no GSTIN"}</small>
                  </td>
                  <td>
                    {i.buyer_name}
                    <br />
                    <small className="muted">{i.buyer_gstin || "no GSTIN"}</small>
                  </td>
                  <td>₹{i.taxable_value.toLocaleString("en-IN")}</td>
                  <td>
                    ₹{(i.cgst_amount + i.sgst_amount + i.igst_amount).toLocaleString("en-IN")}
                    <br />
                    <small className="muted">{i.is_interstate ? "IGST" : "CGST+SGST"}</small>
                  </td>
                  <td><strong>₹{i.total.toLocaleString("en-IN")}</strong></td>
                  <td>
                    <span className={`status-chip status-${i.status.toLowerCase()}`}>
                      {STATUS_LABEL[i.status] || i.status}
                    </span>
                  </td>
                  <td>
                    <small>
                      {i.invoice_date ? new Date(i.invoice_date).toLocaleDateString() : "—"}
                    </small>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="link-button"
                      data-testid={`admin-trade-doc-open-${i.id}`}
                      onClick={() => setDetail(i.id)}
                    >
                      <Eye size={12} /> View →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className="pagination-row" data-testid="admin-trade-doc-pagination">
              <button
                type="button"
                className="ghost-button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid="admin-trade-doc-prev"
              >Previous</button>
              <small className="muted">Page {page} of {totalPages}</small>
              <button
                type="button"
                className="ghost-button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                data-testid="admin-trade-doc-next"
              >Next</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
